import { getHauteursLibresAlongRoute } from '@utils/wfs/getHauteursLibresAlongRoute';
import { logError, logInfo } from '@utils/logger';
import * as turf from '@turf/turf';

/**
 * Calculate a route for oversized/overweight vehicles
 * @param {string} origin - Origin address
 * @param {string} destination - Destination address
 * @param {number} vehicleHeight - Vehicle height in meters (with buffer)
 * @param {Object} dimensions - Additional vehicle dimensions
 * @returns {Promise<Object>} - Route data with structures
 */
export const calculateOSRoute = async (origin, destination, vehicleHeight, dimensions = {}) => {
  try {
    logInfo(`Calculating route from ${origin} to ${destination} for vehicle height ${vehicleHeight}m`);
    
    // Note: vehicleHeight parameter already includes the safety buffer added in OSRouteForm.jsx
    
    // Initialize Google Maps Directions Service
    const directionsService = new google.maps.DirectionsService();
    
    // Request route from Google Maps
    const result = await directionsService.route({
      origin,
      destination,
      travelMode: google.maps.TravelMode.DRIVING,
      avoidHighways: false,
      avoidTolls: false,
      optimizeWaypoints: true
    });
    
    if (!result.routes || result.routes.length === 0) {
      throw new Error('No route found between the specified locations');
    }
    
    // Extract route points and validate coordinates
    const route = result.routes[0];
    const routePoints = route.overview_path.map(point => {
      const lat = point.lat();
      const lng = point.lng();
      
      // Validate coordinates
      if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
        throw new Error('Invalid coordinates received from Google Maps API');
      }
      
      return {
        latitude: lat,
        longitude: lng
      };
    });
    
    // Create a GeoJSON LineString from the route
    const routeLineString = turf.lineString(routePoints.map(p => [p.longitude, p.latitude]));
    
    // Get structures along the route
    const structures = await getHauteursLibresAlongRoute(
      routeLineString,
      0.5, // 500m buffer
      { minHauteur: 0 } // Get all structures regardless of height
    );
    
    // Filter structures based on vehicle height
    const filteredStructures = structures.filter(structure => {
      const clearance = structure.properties?.clearance;
      return !clearance || clearance >= vehicleHeight;
    });
    
    // Calculate route distance and duration
    const distance = route.legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000; // Convert to km
    const durationInSeconds = route.legs.reduce((total, leg) => total + leg.duration.value, 0);
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const duration = `${hours}h ${minutes}m`;
    
    // Prepare route data
    const routeData = {
      directions: result,
      route: routeLineString,
      structures: structures,
      filteredStructures: filteredStructures,
      distance: distance.toFixed(1),
      duration,
      vehicleHeight,
      dimensions,
      origin,
      destination,
      points: routePoints,
      legs: route.legs.map(leg => ({
        distance: leg.distance.text,
        duration: leg.duration.text,
        startAddress: leg.start_address,
        endAddress: leg.end_address,
        steps: leg.steps.map(step => ({
          distance: step.distance.text,
          duration: step.duration.text,
          instructions: step.instructions,
          path: step.path?.map(p => ({ lat: p.lat(), lng: p.lng() })) || []
        }))
      }))
    };
    
    logInfo(`Route calculated successfully with ${structures.length} structures`);
    return routeData;
  } catch (error) {
    logError('Failed to calculate route:', error);
    throw new Error(`Failed to calculate route: ${error.message}`);
  }
};

/**
 * Check if a route is safe for a vehicle with the given height
 * @param {Object} routeData - Route data
 * @param {number} vehicleHeight - Vehicle height in meters
 * @returns {boolean} - Whether the route is safe
 */
export const isRouteSafe = (routeData, vehicleHeight) => {
  if (!routeData || !routeData.structures || !vehicleHeight) {
    return false;
  }
  
  // Check if any structure has insufficient clearance
  return !routeData.structures.some(structure => {
    const clearance = structure.properties?.clearance;
    return clearance && clearance < vehicleHeight;
  });
};

/**
 * Checks if a vehicle can pass under a structure
 * @param {Object} structure - Structure data
 * @param {number} vehicleHeight - Vehicle height in meters
 * @returns {boolean} - True if the vehicle can pass
 */
export const canVehiclePass = (structure, vehicleHeight) => {
  const minHeight = getMinimumHeight(structure);
  // vehicleHeight already includes safety buffer from OSRouteForm.jsx
  return minHeight >= vehicleHeight;
};

/**
 * Get alternative routes for oversized/overweight vehicles
 * @param {string} origin - Origin address
 * @param {string} destination - Destination address
 * @param {number} vehicleHeight - Vehicle height in meters
 * @param {Object} dimensions - Additional vehicle dimensions
 * @returns {Promise<Array>} - Array of alternative routes
 */
export const getAlternativeRoutes = async (origin, destination, vehicleHeight, dimensions = {}) => {
  try {
    // Initialize Google Maps Directions Service
    const directionsService = new google.maps.DirectionsService();
    
    // Request alternative routes from Google Maps
    const result = await directionsService.route({
      origin,
      destination,
      travelMode: google.maps.TravelMode.DRIVING,
      avoidHighways: false,
      avoidTolls: false,
      provideRouteAlternatives: true
    });
    
    if (!result.routes || result.routes.length === 0) {
      throw new Error('No routes found between the specified locations');
    }
    
    // Process each alternative route
    const alternativeRoutes = await Promise.all(result.routes.map(async (route, index) => {
      // Extract route points and validate coordinates
      const routePoints = route.overview_path.map(point => {
        const lat = point.lat();
        const lng = point.lng();
        
        // Validate coordinates
        if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
          throw new Error('Invalid coordinates received from Google Maps API');
        }
        
        return {
          latitude: lat,
          longitude: lng
        };
      });
      
      // Create a GeoJSON LineString from the route
      const routeLineString = turf.lineString(routePoints.map(p => [p.longitude, p.latitude]));
      
      // Get structures along the route
      const structures = await getHauteursLibresAlongRoute(
        routeLineString,
        0.5, // 500m buffer
        { minHauteur: 0 } // Get all structures regardless of height
      );
      
      // Calculate route distance and duration
      const distance = route.legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000; // Convert to km
      const durationInSeconds = route.legs.reduce((total, leg) => total + leg.duration.value, 0);
      const hours = Math.floor(durationInSeconds / 3600);
      const minutes = Math.floor((durationInSeconds % 3600) / 60);
      const duration = `${hours}h ${minutes}m`;
      
      // Check if route is safe
      const isSafe = !structures.some(structure => {
        const clearance = structure.properties?.clearance;
        return clearance && clearance < vehicleHeight;
      });
      
      return {
        index,
        route: routeLineString,
        structures,
        distance: distance.toFixed(1),
        duration,
        isSafe,
        points: routePoints
      };
    }));
    
    return alternativeRoutes;
  } catch (error) {
    logError('Failed to get alternative routes:', error);
    throw new Error(`Failed to get alternative routes: ${error.message}`);
  }
};

export default calculateOSRoute;